import * as Sentry from "@sentry/react";

// Sentry DSN is a publishable identifier — safe to ship in the client bundle.
// Frontend project: tipomatic (us.sentry.io / org 330636).
const DSN = "https://9f9b71cad755db0c88861d3c2f742770@o330636.ingest.us.sentry.io/4511491423862784";

export function initMonitoring() {
  // Only enable in production builds.
  if (!import.meta.env.PROD) return;
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    release: __APP_RELEASE__,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // Filter out noisy / irrelevant errors
    ignoreErrors: [
      // Browser quirks
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications.",
      "Non-Error promise rejection captured",
      // Google Translate / translation extensions mutate the DOM outside React,
      // then React fails to unmount the node it no longer owns.
      "Failed to execute 'removeChild' on 'Node'",
      "Failed to execute 'insertBefore' on 'Node'",
      "The node to be removed is not a child of this node",
      "The object can not be found here",
      // Facebook in-app browser (Android WebView) — bridge is torn down on
      // beforeunload before our listeners fire. Not fixable from web code.
      "Java object is gone",
      "enableButtonsClickedMetaDataLogging",
      "enableDidUserTypeOnKeyboardLogging",
      // iOS in-app browser bridge (Facebook/Instagram/etc.) probing for a
      // native message handler that doesn't exist in real Safari.
      "window.webkit.messageHandlers",
      // Page unloaded while a promise/fetch was in flight — expected on nav.
      "Promise was rejected because the browsing context is going away",
      // Ad blockers block the AdSense request; not our problem.
      "pagead2.googlesyndication.com",
      // Errors thrown by browser extensions injected into the page.
      "Invalid call to runtime.sendMessage",
      "Tab not found",
      "chrome-extension://",
      "moz-extension://",
      "safari-extension://",
    ],
    // Drop events and breadcrumbs originating from the Lovable platform
    // analytics endpoint (/__l5e/trackevents) — not actionable for us.
    beforeSend(event) {
      const url =
        event.request?.url ??
        (event.exception?.values?.[0]?.value as string | undefined) ??
        "";
      if (typeof url === "string" && url.includes("/__l5e/trackevents")) {
        return null;
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      const url = (breadcrumb.data?.url as string | undefined) ?? "";
      if (url.includes("/__l5e/trackevents")) return null;
      return breadcrumb;
    },
  });
}

export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (import.meta.env.PROD && DSN) {
    Sentry.captureException(error, context ? { extra: context } : undefined);
  } else {
    // eslint-disable-next-line no-console
    console.error("[monitoring]", error, context);
  }
}

export function setUserContext(user: { id: string; email?: string } | null) {
  if (!import.meta.env.PROD || !DSN) return;
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email });
  } else {
    Sentry.setUser(null);
  }
}

export { Sentry };
